/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {BrakeChamberPosition, SensorUnits} from '@ion/core/src/types/entities';
import {getUserUnit} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import EventTime from '@ion/dls/src/components/time/EventTime';
import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
import BrakeChamberTrailerWidget from '@ion/dls/src/icons/BrakeChamberTrailerWidget';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import BrakeChamberOverview from './BrakeChamberOverview';
import {CargoSenseSectionProps} from './CargoSenseSection';
import {useSectionStyles} from './sectionStyles';
import {getBMSDataTable} from './SmarterTrailerUtils';

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    detailTombstone : {
      height  : '250px',
      padding : '10px'
    },
    content : {
      padding        : '15px',
      display        : 'flex',
      flexDirection  : 'column',
      justifyContent : 'center'
    }
  };
});

const BrakeChamberSection = ({
  trailerData,
  sensorUpdateLoading
}: CargoSenseSectionProps) => {
  const classes                      = useStyles();
  const sectionClasses               = useSectionStyles();
  const {isCurrent}                  = useThemeContext();
  const {data, messageTime}          = trailerData.BMS;
  const preferredUnit                = getUserUnit() === Unit.METRIC ? SensorUnits.KPA : SensorUnits.PSI;
  const [highlight, setHighlight]    = useState<BrakeChamberPosition>(null);
  const tableData                    = Object.values(getBMSDataTable(data, preferredUnit) || {});
  const isDesktop                    = useIsDesktop();

  const columnFields = [
    translate('common:smarterTrailer.brakeChamber.columns.name'),
    translate('common:smarterTrailer.brakeChamber.columns.pressure', {unit: preferredUnit}),
    translate('common:smarterTrailer.brakeChamber.columns.service', {unit: preferredUnit}),
    translate('common:smarterTrailer.brakeChamber.columns.wear')
  ];

  const setBrakeChamberOverviewHighlight = useCallback((newHighlightedValue) => {
    if (!isDesktop && highlight === newHighlightedValue) {
      setHighlight(null);
    } else {
      setHighlight(newHighlightedValue);
    }
  }, [highlight, isDesktop]);

  if (!sensorUpdateLoading) {
    return (
      <>
        <div id='brake-chamber-content'
          className={themedClassName(classes.content, sectionClasses.currentContent, isCurrent)}>
          <BrakeChamberTrailerWidget data={data}
            highlight={highlight}
            setHighlight={setHighlight} />
          <BrakeChamberOverview data={data}
            highlight={highlight}
            setHighlight={setBrakeChamberOverviewHighlight}
            columns={columnFields}
            tableData={tableData} />
        </div>
        <EventTime eventTime={messageTime} padding={isCurrent ? '0px' : '0px 15px 15px'}/>
      </>
    );
  }
  return (
    <div className={classes.detailTombstone}>
      <TombstoneLoader />
    </div>
  );
};

export default BrakeChamberSection;
